<template>
  <div class="cashapp-wrap">
    <div :id="containerId" ref="cashAppContainer" />
    <p class="cashapp-info">You will be redirected to Cash App to complete payment.</p>
    <div v-if="cashAppIsLoading" class="cashapp-loading">
      <span class="cashapp-spinner" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRecurlyStore } from '@/store/RecurlyStore'

const props = defineProps<{ containerId: string }>()

const recurlyStore = useRecurlyStore()
const { initCashApp, destroyCashApp } = recurlyStore
const { cashAppIsLoading } = storeToRefs(recurlyStore)
const cashAppContainer = shallowRef<HTMLElement | null>(null)
const selector = `#${props.containerId}`

watch(cashAppContainer, el => {
  if (el) {
    initCashApp(selector)
  } else {
    destroyCashApp()
  }
})

onBeforeUnmount(() => destroyCashApp())
</script>

<style scoped lang="scss">
.cashapp-wrap {
  position: relative;
  margin-bottom: $spacer-3;
  border: 2px solid var(--color-border-active);
  border-radius: $border-radius-sm;
  padding: $spacer-2 $spacer-3;
  min-height: 60px;
}

.cashapp-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-primary);
  border-radius: $border-radius-sm;
}

.cashapp-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
  color: var(--color-text-secondary);
}

.cashapp-info {
  margin: 0;
  font-size: 12px;
  line-height: 1.3;
  color: var(--color-text-secondary);
}

// Adyen renders its own DOM into our container with `.adyen-checkout__*` BEM
// classes. We flatten its tile chrome (background, borders, header layout) so
// it visually integrates with our wrap, hide its loading spinner (we render
// our own), and replace its CDN-loaded brand icon with a themed inline SVG.
.cashapp-wrap :deep(.adyen-checkout__payment-method),
.cashapp-wrap :deep(.adyen-checkout__payment-method--selected),
.cashapp-wrap :deep(.adyen-checkout__payment-method__details),
.cashapp-wrap :deep(.adyen-checkout__payment-method__header) {
  background: transparent;
  border: none;
  box-shadow: none;
  margin: 0;
  padding: 0;
  min-height: 0;
  cursor: default;
}

.cashapp-wrap :deep(.adyen-checkout__payment-method__header) {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: $spacer-2;
}

.cashapp-wrap :deep(.adyen-checkout__payment-method__name) {
  color: var(--color-text-primary);
  font-weight: 700;
  font-size: $fs0;
  cursor: default;
  margin: 0;
}

.cashapp-wrap :deep(.adyen-checkout__payment-method__image),
.cashapp-wrap :deep(.adyen-checkout__payment-method__image__wrapper) {
  display: none;
}

.cashapp-wrap :deep(.adyen-checkout__spinner),
.cashapp-wrap :deep(.adyen-checkout__spinner-container),
.cashapp-wrap :deep(.adyen-checkout__loading-input__form) {
  display: none;
}

.cashapp-wrap :deep(.adyen-checkout__payment-method__header::before) {
  content: '';
  display: inline-block;
  flex: 0 0 auto;
  width: 28px;
  height: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='20' viewBox='0 0 28 20' fill='none'><rect width='28' height='20' rx='3.33333' fill='%230D0E0F'/><rect x='0.5' y='0.5' width='27' height='19' rx='2.83333' stroke='%23F1E7D1' stroke-opacity='0.1'/><path d='M14.6726 8.9785C16.7797 9.43008 17.7429 10.303 17.7429 11.778C17.7429 13.6271 16.2372 14.992 13.8868 15.1364L13.6575 16.2302C13.6351 16.3371 13.5407 16.4137 13.4316 16.4137H11.6232C11.4742 16.4137 11.3642 16.2745 11.3987 16.1295L11.6889 14.9064C10.5238 14.5749 9.58633 13.9301 9.03975 13.1341C8.971 13.0339 8.99383 12.897 9.08975 12.8223L10.3537 11.8393C10.4578 11.7583 10.6072 11.7821 10.6842 11.8893C11.3538 12.8213 12.3812 13.3734 13.619 13.3734C14.7327 13.3734 15.5756 12.8316 15.5756 12.0489C15.5756 11.4468 15.1542 11.1759 13.7394 10.8749C11.3012 10.3632 10.3379 9.46017 10.3379 7.98516C10.3379 6.27379 11.7762 4.98101 13.9457 4.8131L14.1826 3.68346C14.205 3.57656 14.2992 3.5 14.4085 3.5H16.1925C16.3397 3.5 16.4492 3.63587 16.4181 3.77971L16.1438 5.04542C17.0774 5.33279 17.8376 5.84662 18.3107 6.48067C18.3841 6.57895 18.3652 6.71812 18.2704 6.79588L17.1173 7.74143C17.0171 7.8236 16.8708 7.80566 16.7893 7.70489C16.2032 6.98003 15.2807 6.57038 14.2812 6.57038C13.1675 6.57038 12.4752 7.05201 12.4752 7.74435C12.4752 8.31628 13.017 8.61733 14.6726 8.9785Z' fill='%23F1E7D1' fill-opacity='0.6'/></svg>");
}
</style>
